import {
  blobToDataUri,
  deserify,
  downloadPdfToAndroid,
  fetchImageAsBase64,
  isAndroidDownloadAvailable,
  getMediaBasePath,
  getTeacherSubjectName,
} from '@geneo2-web/shared-ui';
import { Box } from '@mui/material';
import {
  ContentElementType,
  ImageOption,
  ImageType,
  McqMultipleContentModel,
  McqSingleContentModel,
  OptionsType,
  Question,
  SolutionElementType,
} from '@protos/content_management/content.db_pb';
import {
  useAppDispatch,
  useAppSelector,
} from 'apps/teacher/src/reduxStore/reduxHooks';
import React, { forwardRef, useRef, useState } from 'react';
import { setToastInfo } from '../../../../Home/reducer/homeDashboard.slice';
import { isGifMp4 } from 'libs/shared-ui/src/components/elements/ImageWrapper';
import { QPGPreviewHtml } from './QPGPreviewHtml';
import { default as Geneologo } from 'libs/shared-ui/src/assets/images/geneo-logo.png';
import { getSubjectNameFromEnum } from '../../../../Home/home.utils';
import { generateDocxFromElement, inlineRemoteImagesAsBase64 } from '../../../Common/PrintablePaper/docxExportUtils';
import { renderElementToPdf } from '../../../Common/PrintablePaper/pdfExportUtils';

interface IPropsQPGPrintablePaper {
  printAnswer?: boolean;
  loading?: boolean;
  setLoading?: (loading: boolean) => void;
  printableFontSize?: number;
  SchoolLogo?: string;
}

const QPGPrintablePaper = forwardRef<any, IPropsQPGPrintablePaper>(
  ({ setLoading, printableFontSize, printAnswer, SchoolLogo }, ref) => {
    const printableRef = useRef<HTMLDivElement>(null);
    const dispatch = useAppDispatch?.() || (() => {});
    const [showData, setShowData] = useState(false);
    // Table layout (kept together) for DOCX; flex layout (page-splittable) for PDF.
    const [useDocxLayout, setUseDocxLayout] = useState(false);

    const { qpg_content } = deserify(
      useAppSelector((state) => state.assessment)
    );
    const { user_info } = deserify(useAppSelector((state) => state.auth));

    // ----------------------------------------------------

    const sanitizeFilenamePart = (part: string) => {
      return part
        .replace(/[^a-zA-Z0-9]/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-+|-+$/g, '');
    };

    // ----------------------------------------------------

    const getSubjectsClassFilenamePrefix = () => {
      const sections = qpg_content?.multiSubjectSections ?? [];
      const subjectNames = [
        ...new Set(
          sections
            .map((section) => {
              const name =
                getTeacherSubjectName(
                  section.subjectId,
                  user_info?.teachClassSubjects
                ) || getSubjectNameFromEnum(section.subject);
              return name?.trim();
            })
            .filter((name): name is string => !!name)
        ),
      ];
      const classPart = qpg_content?.assessment?.class
        ? String(qpg_content.assessment.class)
        : '';
      const parts = [...subjectNames];
      if (classPart) parts.push('Class_' + classPart);
      return parts.length ? `${parts.join('_')}_` : '';
    };

    // ----------------------------------------------------

    const getLogoDimensions = (
      imgWidth: number,
      imgHeight: number,
      targetHeight: number
    ) => {
      const ratio = targetHeight / imgHeight;
      return {
        width: imgWidth * ratio,
        height: targetHeight,
      };
    };

    // ----------------------------------------------------

    const loadImage = (src: string) =>
      new Promise<HTMLImageElement>((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.src = src;
      });

    // ----------------------------------------------------

    const questionsList = qpg_content?.assessmentContent ?? [];

    const processElements = async (
      arr: ContentElementType[] | SolutionElementType[]
    ) => {
      if (!Array.isArray(arr)) return arr;
      return Promise.all(
        arr.map(async (element) => {
          if (element?.model?.value instanceof ImageType) {
            const url = element.model.value.imageUrl;
            if (url && !url.startsWith('data:')) {
              let fullUrl = getMediaBasePath(url, 'processedMediaBucket');
              fullUrl = isGifMp4(fullUrl)
                ? fullUrl.replace('mp4', 'jpg')
                : fullUrl;
              const base64 = await fetchImageAsBase64(
                getMediaBasePath(fullUrl, 'processedMediaBucket')
              );
              if (base64) {
                element.model.value.imageUrl = base64;
              }
            }
          }
          return element;
        })
      );
    };

    // ----------------------------------------------------

    const processOptions = async (arr: OptionsType[]) => {
      if (!Array.isArray(arr)) return arr;
      return Promise.all(
        arr.map(async (option) => {
          if (
            option?.optionType?.value instanceof ImageOption &&
            option.optionType.value.image instanceof ImageType
          ) {
            const url = option.optionType.value.image.imageUrl;
            if (url && !url.startsWith('data:')) {
              let fullUrl = getMediaBasePath(url, 'processedMediaBucket');
              fullUrl = isGifMp4(fullUrl)
                ? fullUrl.replace('mp4', 'jpg')
                : fullUrl;
              const base64 = await fetchImageAsBase64(
                getMediaBasePath(fullUrl, 'processedMediaBucket')
              );
              if (base64) {
                option.optionType.value.image.imageUrl = base64;
              }
            }
          }
          return option;
        })
      );
    };

    // ----------------------------------------------------

    React.useImperativeHandle(ref, () => ({
      handlePrint: async (isAnswer = false, title = '', printasdoc = false) => {
        try {
          if (setLoading) setLoading(true);

          // --- Step 1: Preprocess Questions before Rendering ---
          const processedQuestions: Question[] = [];
          if (questionsList) {
            for (let i = 0; i < questionsList.length; i++) {
              const question = new Question(questionsList[i]);

              if (question.question?.model?.value?.commonQuestionContent) {
                // Elements
                question.question.model.value.commonQuestionContent.elements =
                  await processElements(
                    question.question.model.value.commonQuestionContent.elements
                  );

                // Solutions
                question.question.model.value.commonQuestionContent.solution =
                  await processElements(
                    question.question.model.value.commonQuestionContent.solution
                  );

                // Options (for MCQ)
                if (
                  question.question.model.value instanceof
                    McqSingleContentModel ||
                  question.question.model.value instanceof
                    McqMultipleContentModel
                ) {
                  question.question.model.value.options = await processOptions(
                    question.question.model.value.options
                  );
                }
              }

              processedQuestions.push(question);
            }
          }

          // --- Step 2: Render DOM with Processed Data ---
          // setFetchedQuestions(processedQuestions);
          setUseDocxLayout(printasdoc);
          setShowData(true);

          // Allow React render & paint to complete
          await new Promise((r) => setTimeout(r, 300));

          const el = printableRef.current;
          if (!el) throw new Error('Printable element not found');

          // --- Step 3: Wait for images (poll so late ImageWithBase64 swaps are included) ---
          const waitForImagesToLoad = async (container: HTMLElement) => {
            const imgs = Array.from(container.querySelectorAll('img'));
            await Promise.all(
              imgs.map(
                (img) =>
                  new Promise<void>((resolve) => {
                    if (img.complete) return resolve();
                    img.onload = img.onerror = () => resolve();
                  })
              )
            );
          };

          const waitUntilImagesStable = async (
            container: HTMLElement,
            timeoutMs = 3000
          ) => {
            const start = Date.now();
            let lastCount = -1;
            let stableRounds = 0;
            while (Date.now() - start < timeoutMs) {
              await waitForImagesToLoad(container);
              const count = container.querySelectorAll('img').length;
              if (count === lastCount) {
                stableRounds += 1;
                if (stableRounds >= 2) return;
              } else {
                stableRounds = 0;
                lastCount = count;
              }
              await new Promise((r) => setTimeout(r, 150));
            }
          };

          await waitUntilImagesStable(el);

          // --- Step 4: Wait for DOM layout stability ---
          await new Promise((r) => requestAnimationFrame(r));

          // Embed remote images as base64 so PDF and DOC share the same sources
          await inlineRemoteImagesAsBase64(el);

          const assessmentTitle = sanitizeFilenamePart(
            title || qpg_content?.assessment?.assessmentTitle || 'Assessment'
          );
          const fileBaseName = `${getSubjectsClassFilenamePrefix()}${assessmentTitle}${
            isAnswer ? '_Answers' : '_Questions'
          }`;

          const rightLogo = Geneologo;
          const leftLogo = SchoolLogo
            ? await fetchImageAsBase64(SchoolLogo)
            : undefined;

          if (printasdoc) {
            // --- Step 5a: Generate DOCX ---
            await generateDocxFromElement({
              el,
              fontSizePx: printableFontSize,
              title: assessmentTitle,
              filename: `${fileBaseName}.docx`,
            });
          } else {
            // --- Step 5b: Generate PDF (existing flow) ---
            const opt = {
              margin: [12, 12, 25, 12],
              filename: `${fileBaseName}.pdf`,
              image: { type: 'jpeg', quality: 0.98 },
              html2canvas: {
                scale: 2,
                useCORS: true,
              },
              jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
              pagebreak: {
                mode: ['css', 'legacy', 'whiteline'],
                avoid: ['.avoid-break-element', 'img'],
              },
            };

            const pdf = await renderElementToPdf(el, opt);
            const totalPages = pdf.internal.getNumberOfPages();
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const footerY = pageHeight - 8;
            const lineY = footerY - 8;
            const logoY = 5;

            for (let i = 1; i <= totalPages; i++) {
              pdf.setPage(i);

              if (i === 1) {
                if (leftLogo) {
                  const leftLogoHeight = 15;
                  const leftImg = await loadImage(leftLogo);
                  const leftLogoSize = getLogoDimensions(
                    leftImg.width,
                    leftImg.height,
                    leftLogoHeight
                  );
                  pdf.addImage(
                    leftLogo,
                    'PNG',
                    10,
                    logoY,
                    leftLogoSize.width,
                    leftLogoSize.height
                  );

                  const rightLogoHeight = 10;
                  const rightImg = await loadImage(rightLogo);
                  const rightLogoSize = getLogoDimensions(
                    rightImg.width,
                    rightImg.height,
                    rightLogoHeight
                  );
                  pdf.addImage(
                    rightLogo,
                    'PNG',
                    pageWidth - rightLogoSize.width - 10,
                    5,
                    rightLogoSize.width,
                    rightLogoSize.height
                  );
                }
              }

              pdf.setFontSize(10);
              pdf.setDrawColor(150);
              pdf.setLineWidth(0.2);
              pdf.line(10, lineY, pageWidth - 10, lineY);

              if (i === 1) {
                pdf.text('© Copyright reserved.', 10, footerY, {
                  align: 'left',
                });

                pdf.text(
                  `This Paper consists of ${totalPages} printed pages.`,
                  pageWidth / 2,
                  footerY,
                  { align: 'center' }
                );
              } else {
                pdf.text(`Page ${i} of ${totalPages}`, pageWidth / 2, footerY, {
                  align: 'center',
                });
              }
            }

            if (isAndroidDownloadAvailable()) {
              // The native bridge fetches its argument as a URL, which works
              // for a real https signedUrl but can't resolve a page-local
              // blob: URL. Send the PDF as a self-contained base64 data URI
              // instead so native can decode it directly.
              const pdfBlob = pdf.output('blob');
              const dataUri = await blobToDataUri(pdfBlob);
              downloadPdfToAndroid(dataUri, opt.filename);
            } else {
              await pdf.save(opt.filename);
            }
          }

          // --- Step 6: Cleanup ---
          setShowData(false);
          if (setLoading) setLoading(false);
        } catch (error) {
          console.error('Error generating document:', error);

          dispatch(
            setToastInfo({
              label: printasdoc
                ? 'Error while generating doc'
                : 'Error while generating pdf',
              variant: 'error',
              open: true,
            })
          );

          if (setLoading) setLoading(false);
        }
      },
    }));

    return (
      // Off-screen (not display:none) so KaTeX/fonts get real layout for capture
      <Box
        sx={{
          position: 'fixed',
          left: -10000,
          top: 0,
          width: 794,
          pointerEvents: 'none',
          zIndex: -1,
        }}
      >
        {showData && (
          <Box
            ref={printableRef}
            sx={{
              fontSize: printableFontSize
                ? printableFontSize + 'px !important'
                : undefined,
              backgroundColor: '#fff',
            }}
          >
            <QPGPreviewHtml
              printAnswer={printAnswer}
              useTableLayout={useDocxLayout}
            />
          </Box>
        )}
      </Box>
    );
  }
);

export default QPGPrintablePaper;
